import {
  closest,
  COMPLETE_ALT_RE,
  getElement,
  insertHTMLBefore,
  setObject,
  toggleClass,
  translateAltString,
  uid
} from "./utils";
import {arrayRemove, assign} from "./voidash.js";
import {Event, EventEmitter} from "./Events";
import Template from "./Template";
import Sentinel from "./Sentinel";

var TEMPLATE_ID_SUFFIX = "_id";

const defaultViewOptions = {
  ALTS: false,
  ALT: 0
};

const View = EventEmitter.extend({
  constructor: function(model, opts) {
    EventEmitter.call(this);
    this.model = model;
    this.opts = assign(this.getDefaultOptions(), defaultViewOptions, opts);

    var templateEl;
    if((templateEl = getElement(this.opts.template)) instanceof HTMLElement) {
      var template = new Template();
      template.addTemplate("base", templateEl.innerHTML);
      this.opts.template = template;
    }

    this.id = uid(this.getName());
    this.isMounted = false;
    this.beginSentinel = new Sentinel();
    this.endSentinel = new Sentinel();

    this.html = null;

    this.subviews = {};
    this.sentinels = {};
    this.portals = [];

    this.referenceIds = [];
    this.references = {};

    this.templateData = null;

    this.classes = {};
    this.mountCallbacks = [];

    this.altAttrs = null;

    this.init();
  },

  isView : true,

  init: function() {},

  getDefaultOptions: function() {
    return {};
  },

  getName: function() {
    return "view";
  },

  getHTML: function() {
    if(this.html) return this.html;
    if(!this.templateData) this.initTemplateData();
    assign(this.templateData, this.getTemplateData());
    return this.beginSentinel.getHTML() + this.opts.template.parse(this.getTemplateName(), this.templateData) + this.endSentinel.getHTML();
  },

  getTemplateName: function() {
    throw new Error("Template: getTemplateName() not implemented");
  },

  getTemplateData: function() {
    return {};
  },

  handleMounted: function() {},

  handleUnmounted: function() {},

  handleDestroyed: function() {},

  getElement: function(id) {
    return document.getElementById(this.getElementId(id));
  },

  getElementId: function(id) {
    return this.id + "-" + id;
  },

  getNodes: function(includeSentinels = false) {
    const beginSentinel = this.beginSentinel.getReference();
    const endSentinel = this.endSentinel.getReference();

    const begin = beginSentinel;
    const end = endSentinel.nextSibling;

    const nodes = [];

    let node = begin;
    while(node !== end) {
      const next = node.nextSibling;
      if (includeSentinels || (node !== beginSentinel && node !== endSentinel)) nodes.push(node);
      node = next;
    }

    return nodes;
  },

  insertBefore: function(ref) {
    var wasMounted = this.isMounted;

    if(this.isMounted) {
      var begin = this.beginSentinel.getReference();
      var end = this.endSentinel.getReference().nextSibling;
      var parentNode = ref.parentNode;
      if(!closest(ref, node => node._VOID_View_root === this) && ref !== end) {
        var node = begin;
        while(node !== end) {
          var next = node.nextSibling;
          parentNode.insertBefore(node, ref);
          node = next;
        }
      }
    } else {
      insertHTMLBefore(this.html = this.getHTML(), ref);
    }

    this.isMounted = document.documentElement.contains(ref);
    var didMount = !wasMounted && this.isMounted;
    var didUnmount = wasMounted && !this.isMounted;

    if(didMount) {
      // TODO: only if did mount for the first time (?)

      this.placePortalSubviews();
      this.handleDOMReferences();
      this.markRootNodes();
      this.forEachSubview(function(view) { view.handleParentMounted(); });
      this.handleAlts();
      this.applyClasses();
      this.handleMounted();

      this.mountCallbacks.forEach(fn => fn());
      this.mountCallbacks = [];
    } else if(didUnmount) {
      this.handleUnmounted();
    }
  },

  insertBeforeView: function(view) {
    this.insertBefore(view.beginSentinel.getReference());
  },

  insertAfterView: function(view) {
    const endSentinel = view.endSentinel.getReference();
    if (endSentinel.nextSibling) this.insertBefore(endSentinel.nextSibling);
    else this.appendTo(endSentinel.parentNode);
  },

  appendTo: function(el) {
    var ref = document.createComment("");
    el.appendChild(ref);
    this.insertBefore(ref);
    el.removeChild(ref);
  },

  handleParentMounted: function() {
    var wasMounted = this.isMounted;
    this.isMounted = document.documentElement.contains(this.beginSentinel.getReference());
    var didMount = !wasMounted && this.isMounted;

    if(didMount) {
      this.placePortalSubviews();
      this.handleDOMReferences();
      this.markRootNodes();
      this.forEachSubview(function(view) { view.handleParentMounted(); });
      this.handleAlts();
      this.applyClasses();
      this.handleMounted();

      this.mountCallbacks.forEach(fn => fn());
      this.mountCallbacks = [];
    }
  },

  markRootNodes: function() {
    let node = this.beginSentinel.getReference();
    if(node) {
      const end = this.endSentinel.getReference().nextSibling;
      while(node !== end) {
        node._VOID_View_root = this;
        node = node.nextSibling;
      }
    }
  },

  applyClasses: function() {
    var refNames = Object.keys(this.classes);
    for(var i = 0; i < refNames.length; i++) {
      var refName = refNames[i];
      var classStates = this.classes[refName];
      var classNames = Object.keys(classStates);
      var reference = this.references[refName];

      for(var j = 0; j < classNames.length; j++) {
        var className = classNames[j];
        var state = classStates[className];
        toggleClass(reference, className, state);
        delete classNames[className];
      }

      delete this.classes[refName];
    }
  },

  unmount(removeElements = true) {
    if(!this.isMounted) return;

    // TODO: unmount portal subviews
    this.forEachSubview(view => view.unmount(/*removeElements*/false)); // TODO: why pass removeElements if I remove them

    var wasMounted = this.isMounted;
    this.isMounted = false;

    if(removeElements) {
      var el = this.beginSentinel.getReference();
      var parentNode = el.parentNode;
      var end = this.endSentinel.getReference().nextSibling;
      while(el !== end) {
        var next = el.nextSibling;
        parentNode.removeChild(el);
        el = next;
      }
    }

    if(wasMounted) this.handleUnmounted();
  },

  // without place given, subviews register at "undefined"
  registerSubview: function(view, place, index) {
    this.proxy(view);
    /*var record = */this.listenTo(view, "destroy", () => {
      arrayRemove(this.subviews[place], view);
      // removeFromArray(self.listening, record); // TODO: check
    });

    if(place instanceof Element) {
      this.portals.push({ el: place, view: view });
      return;
    } else if(this.subviews.hasOwnProperty(place)) {
      if(arguments.length >= 3) {
        this.subviews[place].splice(index, 0, view);
      } else {
        this.subviews[place].push(view);
      }
    } else {
      this.subviews[place] = [view];
    }

    if(place && this.sentinels.hasOwnProperty(place)) {
      if(arguments.length >= 3 && this.subviews[place].length - 1 > index) {
        view.insertBeforeView(this.subviews[place][index + 1]);
      } else {
        view.insertBefore(this.sentinels[place].getReference());
      }
    }
  },

  addEmptyPlacement: function(place) {
    if(!this.subviews.hasOwnProperty(place)) this.subviews[place] = [];
  },

  destroy: function() {
    this.handleDestroyed();

    var node = this.beginSentinel.getReference();
    if(node) {
      var end = this.endSentinel.getReference().nextSibling;
      while(node !== end) {
        var next = node.nextSibling;
        node.parentNode.removeChild(node);
        node = next;
      }
    }

    this.forEachSubview(function (view) { view.destroy(); });

    EventEmitter.prototype.destroy.call(this);
  },

  placeSubviews: function(places) {
    if(!places) places = Object.keys(this.subviews);
    else if(typeof places === "string") places = [places];

    for(let i = 0; i < places.length; i++) {
      const place = places[i];
      const ref = this.sentinels[place].getReference();
      this.subviews[place].forEach(v => v.insertBefore(ref));
    }
  },

  placePortalSubviews: function() {
    for(var i = 0; i < this.portals.length; i++) {
      var obj = this.portals[i];
      obj.view.appendTo(obj.el);
    }
  },

  handleDOMReferences: function() {
    // this.placeSubviews();

    for(let i = 0; i < this.referenceIds.length; i++) {
      const id = this.referenceIds[i];
      this.references[id] = this.getElement(id);
    }
  },

  handleAlts: function() {
    if (!this.opts.ALTS) return;

    this.destroySubviewsAt("ALTS");
    this.altAttrs = [];

    const START = "[[ALT[[";
    const DELIM = "]]ALT[[";
    const END = "]]ALT]]";

    const visitAttr = (el, {name, value}) => {
      if (COMPLETE_ALT_RE.test(value)) {
        this.altAttrs.push({ el, name, value });
      }
    };

    const visit = (nodes, root) => {
      const pieces = [];
      const originalTextNodes = [];

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        if (node.nodeType === Node.TEXT_NODE) {
          const str = node.textContent;
          const re = /\[\[ALT\[\[|]]ALT\[\[|]]ALT]]/g;
          let match;
          let lastIndex = 0;
          while ((match = re.exec(str))) {
            pieces.push(document.createTextNode(str.substring(lastIndex, match.index)));
            pieces.push(match[0]);
            lastIndex = match.index + match[0].length;
          }

          if (lastIndex > 0) {
            pieces.push(document.createTextNode(str.substring(lastIndex)));
            originalTextNodes.push(node);
          } else {
            pieces.push(node);
          }
        } else {
          pieces.push(node);
          if (node.nodeType === Node.ELEMENT_NODE && (root || !node._VOID_View_root)) {
            for (let j = 0; j < node.attributes.length; j++) visitAttr(node, node.attributes[j]);
            visit(node.childNodes, false);
          }
        }
      }

      if (!originalTextNodes.length) return;

      const newNodes = [];

      for (let i = 0; i < pieces.length; i++) {
        const piece = pieces[i];

        if (piece === START) {
          const delimIdx = pieces.indexOf(DELIM, i);

          if (delimIdx === -1) {
            console.warn(`ALTS: missing ${DELIM} after ${START}`);
            continue;
          }

          const endIdx = pieces.indexOf(END, delimIdx);

          if (endIdx === -1) {
            console.warn(`ALTS: missing ${END} after ${DELIM}`);
            continue;
          }

          const alternatives = [pieces.slice(i + 1, delimIdx), pieces.slice(delimIdx + 1, endIdx)];

          alternatives.forEach(nodes => {
            nodes.forEach(node => {
              if (node.parentNode) node.parentNode.removeChild(node);
            });
          });

          const altsView = new AltsView(null, {
            ALT: this.opts.alt,
            alternatives
          });

          this.registerSubview(altsView, "ALTS");

          newNodes.push(altsView);

          i = endIdx;
        } else {
          newNodes.push(piece);
        }
      }

      const ref = originalTextNodes[0];

      for (let i = 0; i < newNodes.length; i++) {
        const node = newNodes[i];
        if (node instanceof AltsView) {
          node.insertBefore(ref);
        } else {
          ref.parentNode.insertBefore(node, ref);
        }
      }

      originalTextNodes.forEach(n => { n.parentNode.removeChild(n); });
    }

    const rootNodes = [];
    let node = this.beginSentinel.getReference();
    if(node) {
      const end = this.endSentinel.getReference();
      node = node.nextSibling;
      while(node !== end) {
        rootNodes.push(node);
        node = node.nextSibling;
      }
    }

    visit(rootNodes, true);

    this.setAlternative(this.opts.ALT);
  },

  initTemplateData: function() {
    const data = this.templateData = {};

    if (typeof this.opts.lang === "object") assign(data, this.opts.lang);

    const places = Object.keys(this.subviews);

    for(let i = 0; i < places.length; i++) {
      const place = places[i];
      const sentinel = new Sentinel();
      this.sentinels[place] = sentinel;
      const placeViews = this.subviews[place];
      data[place] = "";
      for(let j = 0; j < placeViews.length; j++) {
        const view = placeViews[j];
        data[place] += view.getHTML();
      }
      data[place] += sentinel.getHTML();
    }

    var self = this;
    var varNames = this.opts.template.varNames;

    function visitVarNames(tplName) {
      var idRegExp = new RegExp("^" + tplName + "\\/(.*)" + TEMPLATE_ID_SUFFIX + "$");
      var inclRegExp = new RegExp("^" + tplName + "\\/incl_(.*)$");
      var ifRegExp = new RegExp("^" + tplName + "+\\/if\\s*\\(.+\\)\\s*(.+)$");
      var unlessRegExp = new RegExp("^" + tplName + "+\\/unless\\s*\\(.+\\)\\s*(.+)$");
      var match;

      for(var i = 0; i < varNames.length; i++) {
        if((match = idRegExp.exec(varNames[i]))) {
          data[match[1] + TEMPLATE_ID_SUFFIX] = self.getElementId(match[1]);
          self.referenceIds.push(match[1]);
        } else if((match = inclRegExp.exec(varNames[i])) || (match = ifRegExp.exec(varNames[i])) || (match = unlessRegExp.exec(varNames[i]))) {
          visitVarNames(match[1]);
        }
      }
    }

    visitVarNames(this.getTemplateName());

    assign(data, this.getTemplateData());
  },

  forEachSubview: function(cb, includePortals) {
    var places = Object.keys(this.subviews);
    for(var i = 0; i < places.length; i++) {
      var place = places[i];
      var placeSubviews = this.subviews[place].slice(); // copy array to allow modification of original
      for(var j = 0; j < placeSubviews.length; j++) {
        var view = placeSubviews[j];
        cb(view);
      }
    }

    if (includePortals) {
      this.portals.forEach(({view}) => { cb(view); });
    }
  },

  destroySubviewsAt: function(place) {
    var views = this.subviews[place];
    if(!views) return;
    views = views.slice();
    for(var i = 0; i < views.length; i++) views[i].destroy();
  },

  receiveRoute: function(params) {
    var result = this.route(params);
    if(result !== false) this.forEachSubview(function(view) { view.receiveRoute(params); })
  },

  route: function(params) {},

  emitRoute: function(params) {
    this.emit(new Event("route", this, { params: params }));
  },

  setAlternative: function(alternative) {
    this.opts.ALT = alternative;
    this.forEachSubview(v => { v.setAlternative(alternative); }, true);
    if (this.altAttrs) {
      this.altAttrs.forEach(({el, name, value}) => {
        el.setAttribute(name, this.translateAltString(value));
      });
    }
  },

  translateAltString: function(str, alternative = this.opts.ALT) {
    return translateAltString(str, alternative);
  },

  getDefaultRouteParams: function() {
    return {};
  },

  toggleClass: function(refName, className, state) {
    if(this.isMounted) toggleClass(this.references[refName], className, state);
    else setObject(this.classes, [refName, className], state);
  },

  callNowOrOnMount: function(fn) {
    if (this.isMounted) fn();
    else this.mountCallbacks.push(fn);
  },

  _(str) {
    return this.opts.lang && this.opts.lang[str] || str;
  }/*,

  debug: function(indent) {
    var begin = this.beginSentinel.getReference();
    if(!begin) return;
    var end = this.endSentinel.getReference();
    var frame = document.createElement("div");
    frame.className = "debug_frame";

    var label = document.createElement("div");
    label.className = "debug_label";
    label.innerText = frame.title = this.getName();
    frame.appendChild(label);

    function Rect() {
      this.top = this.right = this.bottom = this.left = 0;
    }

    Rect.prototype.extend = function(rect) {
      if(rect.left !== rect.right) {
        if(this.left === this.right) {
          this.left = rect.left;
          this.right = rect.right;
        }
        else {
          this.left = Math.min(this.left, rect.left);
          this.right = Math.max(this.right, rect.right);
        }
      }

      if(rect.top !== rect.bottom) {
        if(this.top === this.bottom) {
          this.top = rect.top;
          this.bottom = rect.bottom;
        }
        else {
          this.top = Math.min(this.top, rect.top);
          this.bottom = Math.max(this.bottom, rect.bottom);
        }
      }
    };

    function updatePosition() {
      var node = begin.nextSibling;
      if(!node) return;

      var rect = new Rect();

      while(node !== end) {
        switch(node.nodeType) {
          case Node.ELEMENT_NODE:
            rect.extend(node.getBoundingClientRect());
            break;

          case Node.TEXT_NODE:
            var range = document.createRange();
            range.selectNodeContents(node);
            rect.extend(range.getBoundingClientRect());
            break;
        }
        node = node.nextSibling;
      }

      frame.style.left = rect.left + "px";
      frame.style.top = rect.top + "px";
      frame.style.width = (rect.right - rect.left) + "px";
      frame.style.height = (rect.bottom - rect.top) + "px";
    }

    window.addEventListener("load", updatePosition);
    window.addEventListener("resize", updatePosition);
    updatePosition();

    if(!indent) indent = "";

    console.log(indent + this.getName());

    this.forEachSubview(function(view) { view.debug(indent + "  "); });

    document.body.appendChild(frame);
  }*/
});

const AltsView = View.extend({
  init() {
    this.ran = false;
  },

  getHTML() {
    return this.beginSentinel.getHTML() + this.endSentinel.getHTML()
  },

  setAlternative(alternative) {
    if (this.ran) {
      this.opts.alternatives[this.opts.ALT].forEach(n => { n.parentNode.removeChild(n); });
    }

    this.ran = true;
    this.opts.ALT = alternative;

    const ref = this.endSentinel.getReference();
    const parent = ref.parentNode;
    this.opts.alternatives[this.opts.ALT].forEach(n => { parent.insertBefore(n, ref); });
  }
});

export default View;
